import React, { useState, useEffect } from 'react';
import { DataTable, Toast, ReservationFilters, ReturnReasonModal, ConfirmModal, Button } from '../../../shared/components';
import { Eye, ShoppingCart, Package, RotateCcw, X, Download } from 'lucide-react';
import { useReservationManagement } from '../../../shared/hooks';
import adminService from '../../../shared/services/adminService';
import ReservationDetails from './ReservationDetails';

/**
 * Lista de reservas agrupadas con filtros avanzados
 * Permite filtrar por código de retiro, alumno, función y estado
 */
const ReservationList = () => {
  const {
    reservations,
    loading,
    filters,
    pagination,
    fetchGroupedReservations,
    applyReservationFilters,
    applyFiltersNow,
    clearFilters,
    markAsSold,
    markGroupAsSold,
    markGroupAsDelivered,
    returnReservation,
    returnGroupReservations,
    cancelReservation,
    cancelGroupReservations,
    markAsNoCost,
    updatePagination,
    exportGroupedReservations,
  } = useReservationManagement();

  // Estados de modales
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [returnAction, setReturnAction] = useState(null); // 'group' | 'individual'

  // Estados para modales de confirmación
  const [isMarkAsSoldModalOpen, setIsMarkAsSoldModalOpen] = useState(false);
  const [isMarkAsDeliveredModalOpen, setIsMarkAsDeliveredModalOpen] = useState(false);
  const [isCancelGroupModalOpen, setIsCancelGroupModalOpen] = useState(false);
  const [reservationToConfirm, setReservationToConfirm] = useState(null);

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Cargar reservas al montar el componente
  useEffect(() => {
    fetchGroupedReservations();
  }, [fetchGroupedReservations]);

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Manejar búsqueda y filtros
  const handleSearch = async () => {
    applyFiltersNow();
  };

  // Manejar cambio de filtros (solo actualizar estado, no aplicar)
  const handleFiltersChange = (newFilters) => {
    applyReservationFilters(newFilters);
  };

  // Manejar limpieza de filtros
  const handleClearFilters = () => {
    clearFilters();
    fetchGroupedReservations();
  };

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    // Pass the new page directly to fetchGroupedReservations
    // It will handle updating the pagination state
    fetchGroupedReservations({ page: newPage });
  };

  // Manejar marcar como vendida
  const handleMarkAsSold = async (reservation) => {
    setReservationToConfirm(reservation);
    setIsMarkAsSoldModalOpen(true);
  };

  // Confirmar marcar como vendida
  const confirmMarkAsSold = async () => {
    try {
      await markGroupAsSold(reservationToConfirm.accessCodeId);
      showToast('Reserva marcada como vendida exitosamente');
      // Recargar la lista para mostrar el nuevo status
      await fetchGroupedReservations();
    } catch (error) {
      showToast(error.message || 'Error al marcar como vendida', 'error');
    } finally {
      setIsMarkAsSoldModalOpen(false);
      setReservationToConfirm(null);
    }
  };

  // Manejar marcar como entregada
  const handleMarkAsDelivered = async (reservation) => {
    setReservationToConfirm(reservation);
    setIsMarkAsDeliveredModalOpen(true);
  };

  // Confirmar marcar como entregada
  const confirmMarkAsDelivered = async () => {
    try {
      await markGroupAsDelivered(reservationToConfirm.accessCodeId);
      showToast('Reserva marcada como entregada exitosamente');
      // Recargar la lista para mostrar el nuevo status
      await fetchGroupedReservations();
    } catch (error) {
      showToast(error.message || 'Error al marcar como entregada', 'error');
    } finally {
      setIsMarkAsDeliveredModalOpen(false);
      setReservationToConfirm(null);
    }
  };

  // Manejar devolver grupo completo
  const handleReturnGroup = (reservation) => {
    setSelectedReservation(reservation);
    setReturnAction('group');
    setIsReturnModalOpen(true);
  };

  // Confirmar devolución con motivo
  const handleConfirmReturn = async (reason) => {
    setIsReturnModalOpen(false);
    
    try {
      if (returnAction === 'group') {
        await returnGroupReservations(selectedReservation.accessCodeId, reason);
        showToast('Grupo de reservas devuelto exitosamente');
        // Recargar la lista para mostrar el nuevo status
        await fetchGroupedReservations();
      }
    } catch (error) {
      showToast(error.message || 'Error al procesar la devolución', 'error');
    } finally {
      setSelectedReservation(null);
      setReturnAction(null);
    }
  };

  // Manejar cancelar grupo completo
  const handleCancelGroup = async (reservation) => {
    setReservationToConfirm(reservation);
    setIsCancelGroupModalOpen(true);
  };

  // Confirmar cancelar grupo
  const confirmCancelGroup = async () => {
    try {
      await cancelGroupReservations(reservationToConfirm.accessCodeId);
      showToast('Grupo de reservas cancelado exitosamente');
      // Recargar la lista para mostrar el nuevo status
      await fetchGroupedReservations();
    } catch (error) {
      showToast(error.message || 'Error al cancelar el grupo de reservas', 'error');
    } finally {
      setIsCancelGroupModalOpen(false);
      setReservationToConfirm(null);
    }
  };

  // Manejar apertura de modal de detalles
  const handleViewDetails = (reservation) => {
    setSelectedReservation(reservation);
    setIsDetailsModalOpen(true);
  };

  // Manejar cierre de modales
  const handleCloseModals = () => {
    setIsDetailsModalOpen(false);
    setIsReturnModalOpen(false);
    setSelectedReservation(null);
    setReturnAction(null);
  };

  // Manejar exportación
  const handleExport = async () => {
    try {
      await exportGroupedReservations(filters);
      showToast('Reservas exportadas exitosamente');
    } catch (error) {
      showToast(error.message || 'Error al exportar las reservas', 'error');
    }
  };

  // Formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  // Obtener color del estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'sold':
        return 'bg-green-100 text-green-800';
      case 'delivered':
        return 'bg-purple-100 text-purple-800';
      case 'returned':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'blocked':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obtener texto del estado
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Reservado';
      case 'confirmed':
        return 'Confirmada';
      case 'sold':
        return 'Vendida';
      case 'delivered':
        return 'Entregada';
      case 'returned':
        return 'Devuelta';
      case 'cancelled':
        return 'Cancelada';
      case 'completed':
        return 'Completada';
      case 'blocked':
        return 'Bloqueado';
      default:
        return status;
    }
  };

  // Verificar si se puede marcar como vendida
  const canMarkAsSold = (reservation) => {
    return reservation.overallStatus === 'pending' || reservation.overallStatus === 'confirmed';
  };

  // Verificar si se puede marcar como entregada
  const canMarkAsDelivered = (reservation) => {
    return reservation.overallStatus === 'sold' || reservation.overallStatus === 'confirmed';
  };

  // Verificar si se puede devolver (solo si está vendido)
  const canReturn = (reservation) => {
    return reservation.overallStatus === 'sold' || reservation.overallStatus === 'blocked';
  };

  // Verificar si se puede cancelar (solo si está pendiente)
  const canCancel = (reservation) => {
    return reservation.overallStatus === 'pending';
  };

  // Configuración de columnas de la tabla
  const columns = [
    {
      key: 'accessCode',
      label: 'Código de Retiro',
      render: (reservation) => (
        <div className="font-medium text-gray-900">
          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
            {reservation.accessCode}
          </span>
        </div>
      )
    },
    {
      key: 'studentName',
      label: 'Alumno',
      render: (reservation) => (
        <div className="font-medium text-gray-900">
          {reservation.studentName}
        </div>
      )
    },
    {
      key: 'eventTitle',
      label: 'Función',
      render: (reservation) => (
        <div>
          <div className="font-medium text-gray-900">{reservation.eventTitle}</div>
          <div className="text-gray-600 text-sm">
            {reservation.eventDate} - {reservation.eventTime}
          </div>
        </div>
      )
    },
    {
      key: 'totalPrice',
      label: 'Saldo a Pagar',
      render: (reservation) => {
        // Calcular saldo a pagar: solo entradas pendientes
        const pendingAmount = reservation.seats
          ?.filter(seat => seat.status === 'pending')
          ?.reduce((total, seat) => total + (parseFloat(seat.price) || 0), 0) || 0;
        
        return (
          <div className="font-medium text-green-600">
            {formatPrice(pendingAmount)}
          </div>
        );
      }
    },
    {
      key: 'overallStatus',
      label: 'Estado',
      render: (reservation) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(reservation.overallStatus)}`}>
          {getStatusText(reservation.overallStatus)}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (reservation) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewDetails(reservation)}
            className="text-blue-600 hover:text-blue-800 p-1 rounded"
            title="Ver detalles"
          >
            <Eye className="w-4 h-4" />
          </button>
          {canMarkAsSold(reservation) && (
            <button
              onClick={() => handleMarkAsSold(reservation)}
              className="text-green-600 hover:text-green-800 p-1 rounded"
              title="Marcar como vendida"
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          )}
          {canMarkAsDelivered(reservation) && (
            <button
              onClick={() => handleMarkAsDelivered(reservation)}
              className="text-purple-600 hover:text-purple-800 p-1 rounded"
              title="Marcar como entregada"
            >
              <Package className="w-4 h-4" />
            </button>
          )}
          {canReturn(reservation) && (
            <button
              onClick={() => handleReturnGroup(reservation)}
              className="text-orange-600 hover:text-orange-800 p-1 rounded"
              title="Devolver todas las reservas"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          {canCancel(reservation) && (
            <button
              onClick={() => handleCancelGroup(reservation)}
              className="text-red-600 hover:text-red-800 p-1 rounded"
              title="Cancelar todas las reservas"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header con botón de exportar */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestión de Reservas
          </h1>
          <p className="text-gray-600 mt-1">
            Administra y controla las reservas agrupadas por código de acceso
          </p>
        </div>
        
        <Button
          onClick={handleExport}
          variant="outline"
          disabled={loading}
          className="text-green-600 hover:text-green-700 border-green-300 hover:border-green-400"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
      </div>

      {/* Filtros */}
      <ReservationFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        onSearch={handleSearch}
        loading={loading}
      />

      {/* Tabla de reservas */}
      <div className="bg-white rounded-lg shadow">
        <DataTable
          section="Reservas"
          data={reservations}
          columns={columns}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyMessage="No hay reservas disponibles"
          showSearch={false}
          showFilters={false}
        />
      </div>

      {/* Modal de detalles */}
      {selectedReservation && (
        <ReservationDetails
          isOpen={isDetailsModalOpen}
          onClose={handleCloseModals}
          reservation={selectedReservation}
          onMarkAsSold={() => handleMarkAsSold(selectedReservation)}
          onMarkAsDelivered={() => handleMarkAsDelivered(selectedReservation)}
          onReturnReservation={async (reservationId, reason) => {
            await returnReservation(reservationId, reason);
            showToast('Entrada devuelta exitosamente');
            // Recargar la lista para mostrar el nuevo status
            await fetchGroupedReservations();
          }}
          onReturnGroupReservations={async (reason) => {
            await returnGroupReservations(selectedReservation.accessCodeId, reason);
            showToast('Grupo de reservas devuelto exitosamente');
            // Recargar la lista para mostrar el nuevo status
            await fetchGroupedReservations();
          }}
          onCancelReservation={async (reservationId) => {
            try {
              await cancelReservation(reservationId);
              showToast('Reserva cancelada exitosamente');
              // Recargar la lista para mostrar el nuevo status
              await fetchGroupedReservations();
            } catch (error) {
              showToast(error.message || 'Error al cancelar la reserva', 'error');
            }
          }}
          onMarkAsNoCost={async (reservationId, reason) => {
            await markAsNoCost(reservationId, reason);
            // Recargar la lista y refrescar el modal abierto con los datos nuevos
            const response = await fetchGroupedReservations();
            const updated = response?.data?.find((r) => r.id === selectedReservation.id);
            if (updated) setSelectedReservation(updated);
          }}
          onMarkSeatAsSold={async (reservationId) => {
            await markAsSold(reservationId);
            // Recargar la lista y refrescar el modal abierto con los datos nuevos
            const response = await fetchGroupedReservations();
            const updated = response?.data?.find((r) => r.id === selectedReservation.id);
            if (updated) setSelectedReservation(updated);
          }}
          onUpdateExpiry={async (accessCodeId, expiresAt) => {
            try {
              await adminService.updateAccessCodeExpiry(accessCodeId, expiresAt);
              // Recargar la lista y refrescar el modal abierto con la fecha nueva
              const response = await fetchGroupedReservations();
              const updated = response?.data?.find((r) => r.id === selectedReservation.id);
              if (updated) setSelectedReservation(updated);
            } catch (error) {
              const message = error.response?.data?.message || 'Error al actualizar la fecha de vencimiento';
              throw new Error(message);
            }
          }}
          onCancelGroupReservations={async () => {
            try {
              await cancelGroupReservations(selectedReservation.accessCodeId);
              showToast('Grupo de reservas cancelado exitosamente');
              // Recargar la lista para mostrar el nuevo status
              await fetchGroupedReservations();
            } catch (error) {
              showToast(error.message || 'Error al cancelar el grupo de reservas', 'error');
            }
          }}
          canMarkAsSold={canMarkAsSold(selectedReservation)}
          canMarkAsDelivered={canMarkAsDelivered(selectedReservation)}
          canReturn={canReturn}
          canCancel={canCancel}
        />
      )}

      {/* Modal de devolución con motivo */}
      <ReturnReasonModal
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        onConfirm={handleConfirmReturn}
        title="Devolver Reserva"
        description={
          selectedReservation && returnAction === 'group'
            ? selectedReservation?.overallStatus === 'blocked'
              ? `¿Estás seguro de que quieres devolver todas las entradas bloqueadas para este evento?`
              : `¿Estás seguro de que quieres devolver todas las entradas de ${selectedReservation?.studentName}?`
            : ''
        }
        type={returnAction}
        loading={loading}
      />

      {/* Modales de confirmación */}
      <ConfirmModal
        isOpen={isMarkAsSoldModalOpen}
        onClose={() => setIsMarkAsSoldModalOpen(false)}
        onConfirm={confirmMarkAsSold}
        title="Confirmar Venta"
        message={`¿Estás seguro de que quieres marcar como vendida la reserva de ${reservationToConfirm ? (reservationToConfirm.overallStatus === 'blocked' ? 'asientos bloqueados' : reservationToConfirm.studentName) : ''}?`}
        type="warning"
        confirmText="Marcar como Vendida"
        cancelText="Cancelar"
      />

      <ConfirmModal
        isOpen={isMarkAsDeliveredModalOpen}
        onClose={() => setIsMarkAsDeliveredModalOpen(false)}
        onConfirm={confirmMarkAsDelivered}
        title="Confirmar Entrega"
        message={`¿Estás seguro de que quieres marcar como entregada la reserva de ${reservationToConfirm ? (reservationToConfirm.overallStatus === 'blocked' ? 'asientos bloqueados' : reservationToConfirm.studentName) : ''}?`}
        type="warning"
        confirmText="Marcar como Entregada"
        cancelText="Cancelar"
      />

      <ConfirmModal
        isOpen={isCancelGroupModalOpen}
        onClose={() => setIsCancelGroupModalOpen(false)}
        onConfirm={confirmCancelGroup}
        title="Confirmar Cancelación"
        message={`¿Estás seguro de que quieres cancelar todas las reservas de ${reservationToConfirm ? (reservationToConfirm.overallStatus === 'blocked' ? 'asientos bloqueados' : reservationToConfirm.studentName) : ''}?`}
        type="danger"
        confirmText="Cancelar Reservas"
        cancelText="No Cancelar"
      />

      {/* Notificaciones */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  );
};

export default ReservationList; 